import { useEffect, useState } from 'react';

/**
 * Tailwind-aligned breakpoints (px). Identical to MUI's defaults so existing
 * call sites keep their behaviour after removing @mui/material.
 */
const BREAKPOINTS = {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
};

const ORDER = ['xs', 'sm', 'md', 'lg', 'xl'];

function nextBreakpoint(key) {
    const i = ORDER.indexOf(key);
    const next = ORDER[i + 1];
    return next !== undefined ? BREAKPOINTS[next] : Infinity;
}

function mediaQueryFor(query, key) {
    const px = BREAKPOINTS[key] ?? 0;
    const nextPx = nextBreakpoint(key);

    switch (query) {
        case 'up':
            return `(min-width: ${px}px)`;
        case 'down':
            return `(max-width: ${Math.max(0, px - 0.05)}px)`;
        case 'only':
            return `(min-width: ${px}px) and (max-width: ${Math.max(0, nextPx - 0.05)}px)`;
        case 'between':
            return `(min-width: ${px}px)`;
        default:
            return `(min-width: ${px}px)`;
    }
}

/**
 * Drop-in replacement for the old MUI-backed useResponsive. Returns true when
 * the current viewport matches the given query/key combination.
 *
 *   useResponsive('down', 'sm')  // true on phones
 *   useResponsive('up',   'md')  // true on desktop+
 *   useResponsive('only', 'sm')  // true on tablets only
 */
export default function useResponsive(query, key) {
    const mq = mediaQueryFor(query, key);

    const [matches, setMatches] = useState(() =>
        typeof window === 'undefined' ? false : window.matchMedia(mq).matches
    );

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const list = window.matchMedia(mq);
        const onChange = (e) => setMatches(e.matches);
        setMatches(list.matches);
        list.addEventListener('change', onChange);
        return () => list.removeEventListener('change', onChange);
    }, [mq]);

    return matches;
}
